import { motion, MotionConfig } from 'framer-motion';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { HiMapPin } from 'react-icons/hi2';
import { IoCloseSharp } from 'react-icons/io5';
import 'swiper/css';
import 'swiper/css/navigation';
import { Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useAuth, useRegiaoHref } from '../../../components/context/Info';
import LeadContactModal from '../../../components/modals/LeadContactModal';
import BreadcrumbEmpreendimento from '../../../components/pieces/empreendimento/BreadcrumbEmpreendimento';
import ColunaContatoEmpreendimento from '../../../components/pieces/empreendimento/ColunaContatoEmpreendimento';
import { FaixaTituloEmpreendimento, GradeFotosEmpreendimento, PrecoFaixaEmpreendimento } from '../../../components/pieces/empreendimento/TopoFaixaEmpreendimento';
import TopoPainelEmpreendimento from '../../../components/pieces/empreendimento/TopoPainelEmpreendimento';
import { temConversorMoeda } from '../../../utils/moedaSite';
import { getEmpreendimentoServerSideProps } from '../../../lib/server/site-props';
import { imoveis_empreendimento } from '../../../services/properties';
import { getEmpreendimentoById } from '../../../services/selectors';
import { isEmpreendimentoVisibleOnSite } from '../../../utils/empreendimentos';
import { parseYoutubeEmbed } from '../../../utils/youtubeEmbed';
import { titleCasePt } from '../../../utils/titleCasePt';
import { tipoPorIdHomonimo } from '../../../utils/tiposHomonimos';
import {
    clampTexto,
    extraiFaqDaDescricao,
    primeiraFrase,
    sanitizeDescricaoHtml,
} from '../../../utils/descricaoRica';
import {
    buildBreadcrumbSchema,
    buildFaqPageSchema,
    mapEmpreendimentoSchemaType,
} from '../../../utils/schema';
import { ensureReadableTextColor, formatSelects, isColorLight } from '../../../utils/FunctionsGlobals';
import { appendCodsiteQuery, buildCanonical } from '../../../utils/url';
import RecommendationPiecesProperties from '../../imoveis/components/pieces/properties/recommendation/RecommendationPiecesProperties';
import WelcomePiecesProperties2 from '../../imoveis/components/pieces/properties/welcome2/WelcomePiecesProperties';
import styles from './empreendimento.module.css';
import { ehModeloHorizonte, varsHorizonteInterna } from '../../../components/pieces/horizonte/horizonte';
import { ehModeloAurora, hexParaRgba, varsAurora } from '../../../components/pieces/aurora/aurora';
import { ehModeloPanorama, paletaPanorama, varsPanorama } from '../../../components/pieces/panorama/panorama';
import { EASE_LARISSA, ehModeloLarissa, useMovimentoReduzido as useMovimentoReduzidoLr, varsLarissaInterna } from '../../../components/pieces/larissa/larissa';
import { EASE_INTERNACIONAL } from '../../../components/pieces/internacional/internacional';
import { ehModeloInternacional, varsInternacionalPagina } from '../../../components/pieces/internacional/internacionalInternas';
import inInterna from '../../../components/pieces/internacional/InternacionalInterna.module.css';
import { ehModeloNorte, varsNortePagina } from '../../../components/pieces/norte/norteInternas';
import ntInterna from '../../../components/pieces/norte/NorteInterna.module.css';
import '../../../components/pieces/norte/NorteFonte.module.css';
import type { INorteCard } from '../../../components/carousel/apartment/CardNorte';
// Norte (topo 17): as unidades numa GRADE 3/2/1 do card do modelo (card 14), no lugar do carrossel genérico
const NorteCard = dynamic<INorteCard>(() => import('../../../components/carousel/apartment/CardNorte').then((m) => m.NorteCard), { ssr: false });
import { ehModeloCampo, varsCampoPagina, varsCampoRodape } from '../../../components/pieces/campo/campoInternas';
import cpInterna from '../../../components/pieces/campo/CampoInterna.module.css';
import type { ICampoCard } from '../../../components/carousel/apartment/CardCampo';
// Campo (topo 18): as unidades numa GRADE 3/2/1 do card do modelo (card 15), no lugar do carrossel genérico
const CampoCard = dynamic<ICampoCard>(() => import('../../../components/carousel/apartment/CardCampo').then((m) => m.CampoCard), { ssr: false });
import type { IInternacionalCard } from '../../../components/carousel/apartment/CardInternacional';
// Internacional (topo 16): as unidades numa GRADE do card do modelo (card 13), no lugar do carrossel genérico
const InternacionalCard = dynamic<IInternacionalCard>(() => import('../../../components/carousel/apartment/CardInternacional').then((m) => m.InternacionalCard), { ssr: false });
// Internacional (topo 16): depoimentos na MESMA pele da home e da ficha (cabeçalho do modelo, citação grande)
const DepoimentosInternacional = dynamic(() => import('../../../components/pieces/internacional-secoes/DepoimentosInternacional'), { ssr: false });
// Norte (topo 17): depoimentos na MESMA pele da home (DepoimentosNorte do executor HOME — só importado)
const DepoimentosNorte = dynamic(() => import('../../../components/pieces/norte-secoes/DepoimentosNorte'), { ssr: false });
// Campo (topo 18): depoimentos na MESMA pele da home (DepoimentosCampo do executor HOME — só importado)
const DepoimentosCampo = dynamic(() => import('../../../components/pieces/campo-secoes/DepoimentosCampo'), { ssr: false });
import type { ILarissaCard } from '../../../components/carousel/apartment/CardLarissa';
// Larissa (topo 14): as unidades numa GRADE do card do modelo (card 11), no lugar do carrossel genérico
const LarissaCard = dynamic<ILarissaCard>(() => import('../../../components/carousel/apartment/CardLarissa').then((m) => m.LarissaCard), { ssr: false });
import { EASE_METROPOLE, ehModeloMetropole, varsMetropoleInterna } from '../../../components/pieces/metropole/metropole';
import type { IMetropoleCard } from '../../../components/carousel/apartment/CardMetropole';
// Metrópole (topo 15): as unidades numa GRADE do card do modelo (card 12), no lugar do carrossel genérico
const MetropoleCard = dynamic<IMetropoleCard>(() => import('../../../components/carousel/apartment/CardMetropole').then((m) => m.MetropoleCard), { ssr: false });
import { paginaInternaAtelier, siteComPaletaAtelier, varsAtelier } from '../../../components/pieces/atelier/atelier';
import atInterna from '../../../components/pieces/atelier/AtelierInterna.module.css';

const AboutPieces = dynamic(() => import('../../../components/pieces/about/AboutPieces'), { ssr: false });
const AboutPieces1 = dynamic(() => import('../../../components/pieces/about1/AboutPieces1'), { ssr: false });
import { NEW_RODAPE } from '../../../components/registry/home';
import WhatsAppComponent from '../../../components/fixed/whatsapp/WhatsappComponent';
const AboutPieces2 = dynamic(() => import('../../../components/pieces/about1/AboutPieces2'), { ssr: false });
const MegaFooter = dynamic(() => import('../../../components/pieces/mega-footer/MegaFooter'), { ssr: false });
const EditorialFooter = dynamic(() => import('../../../components/pieces/editorial-footer/EditorialFooter'), { ssr: false });
const Mapa = dynamic(() => import('../../../components/pieces/mapa/mapa'), { ssr: false });
const DepoimentoCard2 = dynamic(() => import('../../../components/carousel/depoimentos/DepoimentosCard2'), { ssr: false });
const DepoimentosGrid = dynamic(() => import('../../../components/carousel/depoimentos-grid/DepoimentosGrid'), { ssr: false });
const DepoimentoCard = dynamic(() => import('../../../components/carousel/depoimentos/DepoimentosCard'), { ssr: false });

export const getServerSideProps = getEmpreendimentoServerSideProps;

interface FotoEmpreendimento {
    id: number;
    url_foto: string;
    descricao?: string | null;
    destaque?: number | null;
    posicao?: number | null;
    categoria?: string | null;
}

interface EmpreendimentoListaDetalhe {
    id?: number | null;
    nome?: string | null;
    codsite?: number | null;
}

interface EmpreendimentoCategoriaDetalhe {
    id_categoria?: number | null;
    nome?: string | null;
    codsite?: number | null;
}

interface EmpreendimentoData {
    id: number;
    nome: string;
    id_tipos?: number | null;
    endereco?: string | null;
    cep?: string | null;
    cidade?: string | null;
    bairro?: string | null;
    uf?: string | null;
    Estado?: string | null;
    estado?: string | null;
    id_estado?: number | string | null;
    caracteristicas_detalhes?: EmpreendimentoListaDetalhe[] | null;
    tipos_emp_detalhes?: EmpreendimentoListaDetalhe[] | null;
    categoria_empreendimento?: EmpreendimentoCategoriaDetalhe | null;
    construtora?: string | null;
    descricao?: string | null;
    Descricao_interna?: string | null;
    situacao?: string | null;
    preco_min?: number | null;
    preco_max?: number | null;
    are_privativa_min?: string | null;
    area_privativa_min?: string | null;
    area_privativa_max?: string | null;
    dormitorios?: number | null;
    suites?: number | null;
    banheiros?: number | null;
    vagas?: number | null;
    unidades?: number | null;
    torres?: number | null;
    andares?: number | null;
    mostrar_site?: number | boolean | string | null;
    logo?: string | null;
    meta_description?: string | null;
    meta_keywords?: string | null;
    meta_title?: string | null;
    latitude_longitude?: string | null;
    /** vídeo do condomínio (YouTube) — `tbl_empreendimento.VideosEmpre`, já
     *  servido pela query pública `empreendimento` do site-graphql. */
    VideosEmpre?: string | null;
    /** capa resolvida pelo backend (cai na foto de uma UNIDADE quando o
     *  empreendimento não tem foto própria) — mesmo campo que os cards de
     *  imóvel já usam como fallback (`imovel.empreendimento.foto_destaque`). */
    foto_destaque?: string | null;
    fotos?: FotoEmpreendimento[] | null;
}

interface EmpreendimentoPageProps {
    empreendimento?: EmpreendimentoData | null;
    allDataHome?: any;
    prodOrigin?: string;
    codsite?: number;
    asPath?: string;
}

const fallbackImage = 'https://fotos.sobressai.com.br/Fotos/imovel_sem_foto.jpg';

const parseIdParam = (value: string | string[] | undefined): number | null => {
    const raw = Array.isArray(value) ? value[0] : value;
    if (!raw) return null;
    const parsed = Number(String(raw).replace(/[^\d]/g, ''));
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
};

const stripHtml = (value: string | null | undefined) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const normalizeLocationPart = (value: string | number | null | undefined) =>
    String(value || '')
        .replace(/\s+/g, ' ')
        .replace(/\s*,\s*,+/g, ', ')
        .replace(/^[,\s]+|[,\s]+$/g, '')
        .trim();

const joinLocationParts = (parts: Array<string | number | null | undefined>) =>
    parts
        .map((part) => normalizeLocationPart(part))
        .filter(Boolean)
        .join(', ')
        .replace(/\s*,\s*,+/g, ', ')
        .replace(/^[,\s]+|[,\s]+$/g, '')
        .trim();


const parseAreaNumber = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return null;
    const parsed = Number(String(value).replace(',', '.').replace(/[^\d.]/g, ''));
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
};

const parseLatLngPair = (value: string | null | undefined): [number, number] | null => {
    const source = String(value || '').trim();
    if (!source) return null;

    const numbers = source
        .replace(/point/gi, '')
        .match(/-?\d+(?:[.,]\d+)?/g)
        ?.map((item) => Number(item.replace(',', '.')));

    if (!numbers || numbers.length < 2) return null;

    let lat = Number(numbers[0]);
    let lng = Number(numbers[1]);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) {
        const swap = lat;
        lat = lng;
        lng = swap;
    }

    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

    return [lat, lng];
};

const toMapaLatLng = (value: string | null | undefined) => {
    const coords = parseLatLngPair(value);
    if (!coords) return '';
    return `(${coords[0]},${coords[1]})`;
};

const formatCurrency = (value: number | null | undefined) => {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(amount);
};

const formatNumber = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: value % 1 !== 0 ? 1 : 0,
        maximumFractionDigits: value % 1 !== 0 ? 1 : 0
    }).format(value);

const formatAreaRange = (areaMin: string | number | null | undefined, areaMinAlt: string | number | null | undefined, areaMax: string | number | null | undefined) => {
    const min = parseAreaNumber(areaMin) || parseAreaNumber(areaMinAlt);
    const max = parseAreaNumber(areaMax);

    if (min && max) {
        if (min === max) return `${formatNumber(min)} m²`;
        return `${formatNumber(min)} à ${formatNumber(max)} m²`;
    }
    if (min) return `a partir de ${formatNumber(min)} m²`;
    if (max) return `até ${formatNumber(max)} m²`;
    return '';
};
const formatPriceRange = (minValue: number | null | undefined, maxValue: number | null | undefined) => {
    const min = formatCurrency(minValue);
    const max = formatCurrency(maxValue);

    if (min && max) {
        if (min === max) return min;
        return `${min} a ${max}`;
    }
    if (min) return `a partir de ${min}`;
    if (max) return `ate ${max}`;
    return '';
};

const formatSituacao = (value: string | null | undefined) => {
    const key = String(value || '').toLowerCase();
    const map: Record<string, string> = {
        emconstrucao: 'Em construção',
        lancamento: 'Lançamento',
        pronto: 'Pronto para morar'
    };
    return map[key] || String(value || 'Disponível');
};

const normalizePhone = (value: string | null | undefined) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('55')) return digits;
    if (digits.length >= 10) return `55${digits}`;
    return '';
};

type RgbColor = { r: number; g: number; b: number };

const rgbToHex = ({ r, g, b }: RgbColor) =>
    `#${[r, g, b]
        .map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0'))
        .join('')}`;

const parseRgbColor = (value: string | null | undefined): RgbColor | null => {
    const source = String(value || '').trim().toLowerCase();
    if (!source) return null;

    if (source.startsWith('#')) {
        const normalized = source.slice(1);
        if (/^[0-9a-f]{3}$/.test(normalized)) {
            return {
                r: parseInt(normalized[0] + normalized[0], 16),
                g: parseInt(normalized[1] + normalized[1], 16),
                b: parseInt(normalized[2] + normalized[2], 16)
            };
        }

        if (/^[0-9a-f]{6}$/.test(normalized)) {
            return {
                r: parseInt(normalized.slice(0, 2), 16),
                g: parseInt(normalized.slice(2, 4), 16),
                b: parseInt(normalized.slice(4, 6), 16)
            };
        }
    }

    const rgbMatch = source.match(/^rgba?\(([^)]+)\)$/i);
    if (!rgbMatch) return null;

    const parts = rgbMatch[1].split(',').map((part) => Number(part.trim()));
    if (parts.length < 3 || parts.slice(0, 3).some((part) => !Number.isFinite(part))) return null;

    return {
        r: parts[0],
        g: parts[1],
        b: parts[2]
    };
};

const sanitizeColor = (value: string | null | undefined, fallback: string) => {
    const parsed = parseRgbColor(value);
    if (!parsed) return fallback;
    return rgbToHex(parsed);
};

const getLuminance = (value: string) => {
    const rgb = parseRgbColor(value);
    if (!rgb) return 1;

    const normalizeChannel = (channel: number) => {
        const normalized = channel / 255;
        return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
    };

    const r = normalizeChannel(rgb.r);
    const g = normalizeChannel(rgb.g);
    const b = normalizeChannel(rgb.b);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastRatio = (first: string, second: string) => {
    const lum1 = getLuminance(first);
    const lum2 = getLuminance(second);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
};

const getReadableTextColor = (background: string, light = '#ffffff', dark = '#10223a') =>
    getContrastRatio(background, light) >= getContrastRatio(background, dark) ? light : dark;

const isColorClose = (first: string, second: string, minDistance = 44) => {
    const rgbFirst = parseRgbColor(first);
    const rgbSecond = parseRgbColor(second);
    if (!rgbFirst || !rgbSecond) return first.toLowerCase() === second.toLowerCase();

    const distance = Math.sqrt(
        (rgbFirst.r - rgbSecond.r) ** 2 +
        (rgbFirst.g - rgbSecond.g) ** 2 +
        (rgbFirst.b - rgbSecond.b) ** 2
    );

    return distance < minDistance;
};

const getSafeAccentColor = (candidate: string, background: string) => {
    if (!isColorClose(candidate, background, 52)) return candidate;
    return getLuminance(background) > 0.58 ? '#0a3f67' : '#dbeafe';
};

const decodeHtmlEntities = (value: string) =>
    value
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>');

const normalizeFeatureText = (value: string) =>
    decodeHtmlEntities(stripHtml(value))
        .replace(/^[-*•]+\s*/, '')
        .replace(/\s+/g, ' ')
        .trim();

const uniqueOrdered = (items: string[]) => {
    const seen = new Set<string>();
    const unique: string[] = [];
    items.forEach((item) => {
        const key = item.toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        unique.push(item);
    });
    return unique;
};

const extractCaracteristicas = (html: string | null | undefined) => {
    const source = String(html || '');
    if (!source.trim()) return [];

    const liItems = Array.from(source.matchAll(/<li[^>]*>(.*?)<\/li>/gis))
        .map((match) => normalizeFeatureText(match[1] || ''))
        .filter(Boolean);

    if (liItems.length > 0) {
        return uniqueOrdered(liItems).slice(0, 24);
    }

    const normalized = decodeHtmlEntities(source)
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|h[1-6]|li|ul|ol)>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\u00a0/g, ' ');

    const tokens = normalized
        .split(/\n|;|,/)
        .map((chunk) => normalizeFeatureText(chunk))
        .filter((chunk) => {
            if (!chunk) return false;
            const wordCount = chunk.split(/\s+/).length;
            if (wordCount > 6) return false;
            if (chunk.length < 3 || chunk.length > 46) return false;
            return true;
        });

    return uniqueOrdered(tokens).slice(0, 24);
};

const isPlantaFoto = (foto: FotoEmpreendimento) => String(foto?.categoria || '').trim().toLowerCase() === 'planta';

// Aurora (topo 10) / Panorama (topo 13): as MESMAS vars da raiz da ficha do imóvel ([id].tsx do imóvel) —
// cor do texto da página, gutter comum (--ap-gutter: 16px no celular, 56px no desktop) e, no Panorama,
// Mulish na página toda (--fonte-principal) + os tons "de página" --pn-texto/--pn-destaque/--pn-icone/
// --pn-sutil*/--pn-painel, que leem em fundo claro E escuro. Só roda atrás dos gates (topo 10/13).
const varsAuroraEmpreendimento = (site: any): CSSProperties =>
    ({ ...varsAurora(site), color: 'var(--au-texto)', '--ap-gutter': 'clamp(16px, 4vw, 56px)' } as CSSProperties);

const varsPanoramaEmpreendimento = (site: any): CSSProperties => {
    const fundo = site?.cor5 || '#ffffff';
    const paleta = paletaPanorama(site);
    const claro = isColorLight(fundo);
    const texto = claro ? ensureReadableTextColor(paleta.escuro, fundo) : ensureReadableTextColor(site?.cor13, fundo);
    return {
        ...varsPanorama(site),
        '--fonte-principal': "'Mulish', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        color: texto,
        '--ap-gutter': 'clamp(16px, 4vw, 56px)',
        '--pn-fundo': fundo,
        '--pn-texto': texto,
        '--pn-destaque': claro ? paleta.acentoEscuro : ensureReadableTextColor(paleta.acento, fundo, 3.0, texto, texto),
        '--pn-icone': claro ? paleta.vivo : paleta.ceu,
        '--pn-sutil': hexParaRgba(texto, 0.12),
        '--pn-sutil-forte': hexParaRgba(texto, 0.24),
        '--pn-painel': hexParaRgba(texto, 0.05),
    } as CSSProperties;
};

// Larissa (topo 14): título de seção em DUAS tonalidades (forte + cinza em serifa), como os títulos do modelo.
// Só entra atrás do gate (index.tsx: `larissa ? <TituloLr/> : <texto de sempre>`).
const TituloLr = ({ forte, suave }: { forte: string; suave?: string }) => (
    <>
        {forte}
        {suave ? <span className={styles.lrSuave}> {suave}</span> : null}
    </>
);

// Internacional (topo 16): as unidades do empreendimento numa GRADE do InternacionalCard (.inGrade do empreendimento.module.css,
// 4/3/2/1 colunas) com o cabeçalho de seção do modelo (sobretítulo + título 800). Entrada em cascata como a vitrine da home.
const UnidadesInternacional = ({ imoveis, site, codsite, favorito, nome }: { imoveis: any[]; site: any; codsite: any; favorito: number[]; nome: string }) => {
    const reduzido = useMovimentoReduzidoLr();
    return (
        <MotionConfig reducedMotion="user">
            <div className={styles.inCabecalho}>
                <span className={styles.inSobretitulo}>
                    <span className={styles.inTraco} aria-hidden="true" />
                    No mesmo empreendimento
                </span>
                <h2 className={styles.inTitulo}>Imóveis no {nome}</h2>
            </div>
            <div className={styles.inGrade}>
                {imoveis.map((slide: any, i: number) => (
                    <motion.div
                        key={slide?.id || i}
                        className={styles.inCelula}
                        initial={reduzido ? false : { opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.12 }}
                        transition={reduzido ? { duration: 0 } : { duration: 0.7, delay: (i % 4) * 0.08, ease: EASE_INTERNACIONAL }}
                    >
                        <InternacionalCard slide={slide} site={site} codsite={codsite} favorito={favorito} />
                    </motion.div>
                ))}
            </div>
        </MotionConfig>
    );
};

// Norte (topo 17): as unidades do empreendimento numa GRADE 3/2/1 do NorteCard (NorteInterna .grade) com o cabeçalho de
// seção do modelo (h2 sans 32/500 na tinta). Entrada leve em cascata como a grade do /imoveis.
const UnidadesNorte = ({ imoveis, site, codsite, favorito, nome }: { imoveis: any[]; site: any; codsite: any; favorito: number[]; nome: string }) => {
    const reduzido = useMovimentoReduzidoLr();
    return (
        <MotionConfig reducedMotion="user">
            <div className={ntInterna.cabecalho}>
                <h2 className={ntInterna.titulo}>Imóveis no {nome}</h2>
            </div>
            <div className={ntInterna.grade}>
                {imoveis.map((slide: any, i: number) => (
                    <motion.div
                        key={slide?.id || i}
                        className={ntInterna.celula}
                        initial={reduzido ? false : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.12 }}
                        transition={reduzido ? { duration: 0 } : { duration: 0.5, delay: (i % 3) * 0.06, ease: EASE_INTERNACIONAL }}
                    >
                        <NorteCard slide={slide} site={site} codsite={codsite} favorito={favorito} />
                    </motion.div>
                ))}
            </div>
        </MotionConfig>
    );
};

// Campo (topo 18): as unidades do empreendimento numa GRADE 3/2/1 do CampoCard (CampoInterna .grade, vão 24) com o
// cabeçalho de seção do modelo (sobretítulo em versalete no acento + h2 32/800). Entrada leve em cascata.
const UnidadesCampo = ({ imoveis, site, codsite, favorito, nome }: { imoveis: any[]; site: any; codsite: any; favorito: number[]; nome: string }) => {
    const reduzido = useMovimentoReduzidoLr();
    return (
        <MotionConfig reducedMotion="user">
            <div className={cpInterna.cabecalho}>
                <div>
                    <p className={cpInterna.sobretitulo}>Unidades</p>
                    <h2 className={cpInterna.titulo}>Imóveis no {nome}</h2>
                </div>
            </div>
            <div className={cpInterna.grade}>
                {imoveis.map((slide: any, i: number) => (
                    <motion.div
                        key={slide?.id || i}
                        className={cpInterna.celula}
                        initial={reduzido ? false : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.12 }}
                        transition={reduzido ? { duration: 0 } : { duration: 0.5, delay: (i % 3) * 0.06, ease: EASE_INTERNACIONAL }}
                    >
                        <CampoCard slide={slide} site={site} codsite={codsite} favorito={favorito} />
                    </motion.div>
                ))}
            </div>
        </MotionConfig>
    );
};

// Larissa (topo 14): as unidades do empreendimento numa GRADE do LarissaCard (4/3/2/1 colunas, .lrGrade do
// empreendimento.module.css) com o cabeçalho de seção do modelo (sobretítulo com traço + título em dois tons),
// no lugar do carrossel genérico do RecommendationPiecesProperties. Entrada em cascata como a vitrine da home.
const UnidadesLarissa = ({ imoveis, site, codsite, favorito, nome }: { imoveis: any[]; site: any; codsite: any; favorito: number[]; nome: string }) => {
    const reduzido = useMovimentoReduzidoLr();
    return (
        <MotionConfig reducedMotion="user">
            <div className={styles.lrCabecalho}>
                <span className={styles.lrSobretitulo}>
                    <span className={styles.lrTraco} aria-hidden="true" />
                    No mesmo empreendimento
                </span>
                <h2 className={styles.lrTitulo}>
                    <TituloLr forte="Imóveis" suave={`no ${nome}`} />
                </h2>
            </div>
            <div className={styles.lrGrade}>
                {imoveis.map((slide: any, i: number) => (
                    <motion.div
                        key={slide?.id || i}
                        className={styles.lrCelula}
                        initial={reduzido ? false : { opacity: 0, y: 28 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.12 }}
                        transition={reduzido ? { duration: 0 } : { duration: 0.7, delay: (i % 4) * 0.08, ease: EASE_LARISSA }}
                    >
                        <LarissaCard slide={slide} site={site} codsite={codsite} favorito={favorito} />
                    </motion.div>
                ))}
            </div>
        </MotionConfig>
    );
};

// Metrópole (topo 15): as unidades do empreendimento numa GRADE do MetropoleCard (3/2/1 colunas, .mtGrade do
// empreendimento.module.css) com o cabeçalho de seção do modelo (sobretítulo discreto + título leve de um tom só),
// no lugar do carrossel genérico do RecommendationPiecesProperties. Entrada em cascata como a vitrine da home.
const UnidadesMetropole = ({ imoveis, site, codsite, favorito, nome }: { imoveis: any[]; site: any; codsite: any; favorito: number[]; nome: string }) => {
    const reduzido = useMovimentoReduzidoLr();
    return (
        <MotionConfig reducedMotion="user">
            <div className={styles.mtCabecalho}>
                <span className={styles.mtSobretitulo}>No mesmo empreendimento</span>
                <h2 className={styles.mtTitulo}>Imóveis no {nome}</h2>
            </div>
            <div className={styles.mtGrade}>
                {imoveis.map((slide: any, i: number) => (
                    <motion.div
                        key={slide?.id || i}
                        className={styles.mtCelula}
                        initial={reduzido ? false : { opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.12 }}
                        transition={reduzido ? { duration: 0 } : { duration: 0.7, delay: (i % 3) * 0.08, ease: EASE_METROPOLE }}
                    >
                        <MetropoleCard slide={slide} site={site} codsite={codsite} favorito={favorito} />
                    </motion.div>
                ))}
            </div>
        </MotionConfig>
    );
};

type ResumoIconType = 'dormitorios' | 'area' | 'banheiros' | 'vagas' | 'bairro';

const ResumoTagIcon = ({ type }: { type: ResumoIconType }) => {
    if (type === 'bairro') {
        return <HiMapPin className={styles.tagIcon} />;
    }

    if (type === 'dormitorios') {
        return (
            <svg viewBox="0 0 640 512" className={styles.tagIcon} fill="currentColor" aria-hidden="true">
                <path d="M176 256c44.11 0 80-35.89 80-80s-35.89-80-80-80-80 35.89-80 80 35.89 80 80 80zm352-128H304c-8.84 0-16 7.16-16 16v144H64V80c0-8.84-7.16-16-16-16H16C7.16 64 0 71.16 0 80v352c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16v-48h512v48c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16V240c0-61.86-50.14-112-112-112z" />
            </svg>
        );
    }

    if (type === 'banheiros') {
        return (
            <svg viewBox="0 0 512 512" className={styles.tagIcon} fill="currentColor" aria-hidden="true">
                <path d="M32 384a95.4 95.4 0 0 0 32 71.09V496a16 16 0 0 0 16 16h32a16 16 0 0 0 16-16v-16h256v16a16 16 0 0 0 16 16h32a16 16 0 0 0 16-16v-40.91A95.4 95.4 0 0 0 480 384v-48H32zm464-128H80V69.25a21.26 21.26 0 0 1 36.28-15l19.27 19.26c-13.13 29.88-7.61 59.11 8.62 79.73l-.17.17a16 16 0 0 0 0 22.59l11.31 11.31a16 16 0 0 0 22.63 0L283.31 81.94a16 16 0 0 0 0-22.63L272 48a16 16 0 0 0-22.62 0l-.17.17c-20.62-16.23-49.83-21.75-79.73-8.62l-19.26-19.27A69.25 69.25 0 0 0 32 69.25V256H16a16 16 0 0 0-16 16v16a16 16 0 0 0 16 16h480a16 16 0 0 0 16-16v-16a16 16 0 0 0-16-16z" />
            </svg>
        );
    }

    if (type === 'vagas') {
        return (
            <svg viewBox="0 0 512 512" className={styles.tagIcon} fill="currentColor" aria-hidden="true">
                <path d="M499.99 176h-59.87l-16.64-41.6C406.38 91.63 365.57 64 319.5 64h-127c-46.06 0-86.88 27.63-103.99 70.4L71.87 176H12.01C4.2 176-1.53 183.34.37 190.91l6 24C7.7 220.25 12.5 224 18.01 224h20.07C24.65 235.73 16 252.78 16 272v48c0 16.12 6.16 30.67 16 41.93V416c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-32h256v32c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-54.07c9.84-11.25 16-25.8 16-41.93v-48c0-19.22-8.65-36.27-22.07-48H494c5.51 0 10.31-3.75 11.64-9.09l6-24c1.89-7.57-3.84-14.91-11.65-14.91zm-352.06-17.83c7.29-18.22 24.94-30.17 44.57-30.17h127c19.63 0 37.28 11.95 44.57 30.17L384 208H128l19.93-49.83zM96 319.8c-19.2 0-32-12.76-32-31.9S76.8 256 96 256s48 28.71 48 47.85-28.8 15.95-48 15.95zm320 0c-19.2 0-48 3.19-48-15.95S396.8 256 416 256s32 12.76 32 31.9-12.8 31.9-32 31.9z" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 512 512" className={styles.tagIcon} fill="currentColor" aria-hidden="true">
            <path d="M488 352h-40V109.25l59.31-59.31c6.25-6.25 6.25-16.38 0-22.63L484.69 4.69c-6.25-6.25-16.38-6.25-22.63 0L402.75 64H192v96h114.75L160 306.75V24c0-13.26-10.75-24-24-24H88C74.75 0 64 10.74 64 24v40H24C10.75 64 0 74.74 0 88v48c0 13.25 10.75 24 24 24h40v264c0 13.25 10.75 24 24 24h232v-96H205.25L352 205.25V488c0 13.25 10.75 24 24 24h48c13.25 0 24-10.75 24-24v-40h40c13.25 0 24-10.75 24-24v-48c0-13.26-10.75-24-24-24z" />
        </svg>
    );
};

const EmpreendimentoPage = ({ empreendimento, allDataHome, prodOrigin, codsite, asPath }: EmpreendimentoPageProps) => {
    const router = useRouter();
    const { favorito, setFavorito, isMobile1 } = useAuth();
    // Atelier (topo 12): paleta do modelo (papel/tinta/acento); fora do gate devolve o MESMO objeto.
    const site = siteComPaletaAtelier(allDataHome?.site);
    const pageThemeVars = useMemo(
        () => {
            const background = sanitizeColor(site?.cor5, '#ffffff');
            const paper = sanitizeColor(site?.cor5 || site?.cor4, '#ffffff');
            const surfaceSoft = sanitizeColor(site?.cor5, '#f4f7fa');
            const textBase = sanitizeColor(site?.cor11, '#1a2f42');
            const text = getContrastRatio(background, textBase) >= 3 ? textBase : getReadableTextColor(background);

            const mutedBase = sanitizeColor(site?.cor14 || site?.cor10, '#5f6f7d');
            const muted = getContrastRatio(background, mutedBase) >= 2.5 && !isColorClose(background, mutedBase, 28)
                ? mutedBase
                : (getLuminance(background) > 0.58 ? '#5f6f7d' : '#cbd5e1');

            const lineBase = sanitizeColor(site?.cor14 || site?.cor10, '#cbd5e1');
            const line = !isColorClose(background, lineBase, 28)
                ? lineBase
                : (getLuminance(background) > 0.58 ? '#d0d5dd' : '#475467');

            const accentRaw = sanitizeColor(site?.cor2, '#0a3f67');
            const accent = getSafeAccentColor(accentRaw, background);
            const accentSoftBase = sanitizeColor(site?.cor1 || site?.cor6, '#1f1f1f');
            const accentSoftSeed = getSafeAccentColor(accentSoftBase, background);
            const accentSoft = isColorClose(accentSoftSeed, accent, 34)
                ? (getLuminance(accent) > 0.58 ? '#0a3f67' : '#1f3b52')
                : accentSoftSeed;

            // Texto sobre a cor2 (selo, aba ativa, "Quero mais informações", faixa Localização): a regra do
            // selo "Venda" dos cards (CardApartamentImersivo.tsx:56-57) — cor11/cor13 do site quando passa
            // no AA 4,5:1, senão #111/#fff. O imóvel usa a mesma escolha sem checar contraste.
            const accentInk = ensureReadableTextColor(isColorLight(site?.cor2) ? site?.cor11 : site?.cor13, accent);
            // Cor da fonte sobre o fundo (cor_font do imóvel: isColorLight(cor5) ? cor11 : cor13), com AA.
            const corFonte = ensureReadableTextColor(isColorLight(site?.cor5) ? site?.cor11 : site?.cor13, background);
            const corFonteRgb = parseRgbColor(corFonte);
            const linhaFonte = corFonteRgb ? `rgba(${corFonteRgb.r}, ${corFonteRgb.g}, ${corFonteRgb.b}, 0.1)` : 'rgba(0, 0, 0, 0.1)';
            const accentSoftInk = getReadableTextColor(accentSoft);
            const secondaryInkCandidate = isColorClose(accent, surfaceSoft, 38) ? getReadableTextColor(surfaceSoft) : accent;
            const secondaryInk = getContrastRatio(surfaceSoft, secondaryInkCandidate) >= 3
                ? secondaryInkCandidate
                : getReadableTextColor(surfaceSoft);
            const whatsappInkCandidate = isColorClose(accent, paper, 38) ? getReadableTextColor(paper) : accent;
            const whatsappInk = getContrastRatio(paper, whatsappInkCandidate) >= 3
                ? whatsappInkCandidate
                : getReadableTextColor(paper);
            return {
                '--site-cor1': site?.cor1 || undefined,
                '--site-cor2': site?.cor2 || undefined,
                '--site-cor3': site?.cor3 || undefined,
                '--site-cor4': site?.cor4 || undefined,
                '--site-cor5': site?.cor5 || undefined,
                '--site-cor6': site?.cor6 || undefined,
                '--site-cor7': site?.cor7 || undefined,
                '--site-cor8': site?.cor8 || undefined,
                '--site-cor9': site?.cor9 || undefined,
                '--site-cor10': site?.cor10 || undefined,
                '--site-cor11': site?.cor11 || undefined,
                '--site-cor12': site?.cor12 || undefined,
                '--site-cor13': site?.cor13 || undefined,
                '--site-cor14': site?.cor14 || undefined,
                '--site-cor15': site?.cor15 || undefined,
                '--theme-bg': background,
                '--theme-paper': paper,
                '--theme-surface-soft': surfaceSoft,
                '--theme-text': text,
                '--theme-muted': muted,
                '--theme-line': line,
                '--theme-accent': accent,
                '--theme-accent-soft': accentSoft,
                '--theme-accent-ink': accentInk,
                '--theme-accent-soft-ink': accentSoftInk,
                '--theme-secondary-ink': secondaryInk,
                '--theme-whatsapp-ink': whatsappInk,
                '--emp-cor-fonte': corFonte,
                '--emp-linha': linhaFonte,
                '--emp-swiper-nav': site?.cor2 || 'gray'
            } as CSSProperties;
        },
        [
            site?.cor1,
            site?.cor2,
            site?.cor3,
            site?.cor4,
            site?.cor5,
            site?.cor6,
            site?.cor7,
            site?.cor8,
            site?.cor9,
            site?.cor10,
            site?.cor11,
            site?.cor12,
            site?.cor13,
            site?.cor14,
            site?.cor15
        ]
    );

    const empreendimentoInicial = empreendimento && isEmpreendimentoVisibleOnSite(empreendimento?.mostrar_site)
        ? empreendimento
        : null;
    const [empreendimentoAtual, setEmpreendimentoAtual] = useState<EmpreendimentoData | null>(empreendimentoInicial);
    const [carregando, setCarregando] = useState<boolean>(!empreendimentoInicial);
    const [propertiesRelatedActual, setPropertiesRelatedActual] = useState<any[]>([]);
    const [plantaAtivaIndex, setPlantaAtivaIndex] = useState<number>(0);
    const [modalAberto, setModalAberto] = useState<boolean>(false);
    const [modalInitialIndex, setModalInitialIndex] = useState<number>(0);
    const [openMapa, setOpenMapa] = useState<number>(0);
    const [leadModalOpen, setLeadModalOpen] = useState<boolean>(false);

    const favoritoList = Array.isArray(favorito) ? favorito : [];
    const allCities = allDataHome?.cidades ? formatSelects(allDataHome?.cidades) : [];
    useEffect(() => {
        const empreendimentoValido = empreendimento && isEmpreendimentoVisibleOnSite(empreendimento?.mostrar_site)
            ? empreendimento
            : null;
        setEmpreendimentoAtual(empreendimentoValido);
        setCarregando(!empreendimentoValido);
        setPlantaAtivaIndex(0);
    }, [empreendimento]);

    useEffect(() => {
        if (!router.isReady) return;

        const id = parseIdParam(router.query.id);
        if (!id) {
            setCarregando(false);
            return;
        }

        if (empreendimentoAtual?.id === id) {
            setCarregando(false);
            return;
        }

        let isActive = true;
        setCarregando(true);

        getEmpreendimentoById(id, prodOrigin, codsite)
            .then((response) => {
                if (!isActive) return;
                const empreendimentoResponse = response?.empreendimento || null;
                setEmpreendimentoAtual(
                    empreendimentoResponse && isEmpreendimentoVisibleOnSite(empreendimentoResponse?.mostrar_site)
                        ? empreendimentoResponse
                        : null
                );
                setPlantaAtivaIndex(0);
            })
            .catch(() => {
                if (!isActive) return;
                setEmpreendimentoAtual(null);
            })
            .finally(() => {
                if (!isActive) return;
                setCarregando(false);
            });

        return () => {
            isActive = false;
        };
    }, [codsite, empreendimentoAtual?.id, prodOrigin, router.isReady, router.query.id]);

    useEffect(() => {
        if (!empreendimentoAtual?.id) {
            setPropertiesRelatedActual([]);
            return;
        }

        let isActive = true;

        const getRelatedProperties = async () => {
            try {
                const response = await imoveis_empreendimento(empreendimentoAtual.id, prodOrigin, codsite);
                if (!isActive) return;
                setPropertiesRelatedActual(response?.imoveis?.imoveis || []);
            } catch (_error) {
                if (!isActive) return;
                setPropertiesRelatedActual([]);
            }
        };

        getRelatedProperties();

        return () => {
            isActive = false;
        };
    }, [codsite, empreendimentoAtual?.id, prodOrigin]);

    useEffect(() => {
        if (!modalAberto || typeof document === 'undefined') return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [modalAberto]);

    const fotosOrdenadas = useMemo(() => {
        const photos = [...(empreendimentoAtual?.fotos || [])].filter((foto) => Boolean(foto?.url_foto));
        return photos.sort((a, b) => Number(a.posicao || 0) - Number(b.posicao || 0));
    }, [empreendimentoAtual?.fotos]);

    const fotosPlantas = useMemo(() => fotosOrdenadas.filter((foto) => isPlantaFoto(foto)), [fotosOrdenadas]);

    const fotosGaleria = useMemo(() => {
        const fotosSemPlanta = fotosOrdenadas.filter((foto) => !isPlantaFoto(foto));
        return fotosSemPlanta.length > 0 ? fotosSemPlanta : fotosOrdenadas;
    }, [fotosOrdenadas]);

    useEffect(() => {
        if (plantaAtivaIndex <= fotosPlantas.length - 1) return;
        setPlantaAtivaIndex(0);
    }, [fotosPlantas.length, plantaAtivaIndex]);

    const fotoDestaque = useMemo(() => {
        const destaque = fotosOrdenadas.find((foto) => Number(foto?.destaque) === 1);
        return destaque || fotosGaleria[0] || fotosOrdenadas[0] || null;
    }, [fotosGaleria, fotosOrdenadas]);

    const faixaPreco = formatPriceRange(empreendimentoAtual?.preco_min, empreendimentoAtual?.preco_max);
    const precoInicial = formatCurrency(empreendimentoAtual?.preco_min) || formatCurrency(empreendimentoAtual?.preco_max);
    const faixaArea = formatAreaRange(
        empreendimentoAtual?.area_privativa_min,
        empreendimentoAtual?.are_privativa_min,
        empreendimentoAtual?.area_privativa_max
    );

    const tiposEmpreendimentoNomes = useMemo(() => {
        if (!Array.isArray(empreendimentoAtual?.tipos_emp_detalhes)) return [];
        const tiposDetalhes = empreendimentoAtual?.tipos_emp_detalhes || [];
        return uniqueOrdered(
            tiposDetalhes
                .map((item) => String(item?.nome || '').trim())
                .filter(Boolean)
        );
    }, [empreendimentoAtual?.tipos_emp_detalhes]);

    const tipoNome = useMemo(() => {
        if (tiposEmpreendimentoNomes.length > 0) {
            return tiposEmpreendimentoNomes[0];
        }

        const idTipo = empreendimentoAtual?.id_tipos;
        if (!idTipo || !allDataHome?.tipos?.length) return '';
        const type = allDataHome.tipos.find((item: any) => Number(item?.id) === Number(idTipo))
            || tipoPorIdHomonimo(allDataHome.tipos, idTipo);
        return type?.nome || '';
    }, [allDataHome?.tipos, empreendimentoAtual?.id_tipos, tiposEmpreendimentoNomes]);

    const tiposEmpreendimento = useMemo(() => {
        if (tiposEmpreendimentoNomes.length > 0) {
            return tiposEmpreendimentoNomes;
        }
        return tipoNome ? [tipoNome] : [];
    }, [tipoNome, tiposEmpreendimentoNomes]);

    const categoriaEmpreendimento = useMemo(
        () => String(empreendimentoAtual?.categoria_empreendimento?.nome || '').trim(),
        [empreendimentoAtual?.categoria_empreendimento?.nome]
    );

    const ufEmpreendimento = useMemo(() => {
        const uf = String(empreendimentoAtual?.uf || '').trim();
        if (!uf) return '';

        const idEstado = Number(empreendimentoAtual?.id_estado || 0);
        const nomeEstado = String(empreendimentoAtual?.Estado || empreendimentoAtual?.estado || '').trim();

        if (uf.toUpperCase() === 'RS' && !idEstado && !nomeEstado) {
            return '';
        }

        return uf;
    }, [empreendimentoAtual?.Estado, empreendimentoAtual?.estado, empreendimentoAtual?.id_estado, empreendimentoAtual?.uf]);

    const localizacaoCompleta = useMemo(() => {
        const cidadeUf = [normalizeLocationPart(empreendimentoAtual?.cidade), normalizeLocationPart(ufEmpreendimento)]
            .filter(Boolean)
            .join(' - ');
        return joinLocationParts([empreendimentoAtual?.endereco, empreendimentoAtual?.bairro, cidadeUf]);
    }, [empreendimentoAtual?.bairro, empreendimentoAtual?.cidade, empreendimentoAtual?.endereco, ufEmpreendimento]);

    const resumoTags = useMemo(() => {
        const tags: Array<{ key: string; icon: ResumoIconType; label: string }> = [
            {
                key: 'dormitorios',
                icon: 'dormitorios',
                label: empreendimentoAtual?.dormitorios
                    ? `${empreendimentoAtual?.dormitorios} dorms`
                    : empreendimentoAtual?.suites
                      ? `${empreendimentoAtual?.suites} suíte${empreendimentoAtual?.suites > 1 ? 's' : ''}`
                      : ''
            },
            { key: 'area', icon: 'area', label: (faixaArea || '').replace('à', 'a') },
            { key: 'banheiros', icon: 'banheiros', label: empreendimentoAtual?.banheiros ? `até ${empreendimentoAtual?.banheiros} Banheiros` : '' },
            { key: 'vagas', icon: 'vagas', label: empreendimentoAtual?.vagas ? `${empreendimentoAtual?.vagas} Vagas` : '' },
            { key: 'bairro', icon: 'bairro', label: empreendimentoAtual?.bairro ? `Bairro ${empreendimentoAtual?.bairro}` : '' }
        ];

        return tags.filter((tag) => Boolean(tag.label));
    }, [
        empreendimentoAtual?.bairro,
        empreendimentoAtual?.banheiros,
        empreendimentoAtual?.dormitorios,
        empreendimentoAtual?.suites,
        empreendimentoAtual?.vagas,
        faixaArea
    ]);

    // SEO/GEO: `descricao` é a coluna legada DescricaoEmpre (HTML escrito no CRM).
    // Passa pelo sanitizador (allowlist de tags, atributos zerados) ANTES de ir
    // pro dangerouslySetInnerHTML — antes ia crua.
    const descricaoBruta = empreendimentoAtual?.descricao || empreendimentoAtual?.Descricao_interna || '';
    const descricaoSegura = useMemo(() => sanitizeDescricaoHtml(descricaoBruta), [descricaoBruta]);
    const descricaoHtml = descricaoSegura || '<p>Descrição indisponível.</p>';
    // FAQPage: pares <h3>?</h3><p></p> depois de um <h2>Perguntas frequentes</h2>.
    // Sem a seção → [] → nenhum <script> de FAQ é emitido.
    const faqPares = useMemo(() => extraiFaqDaDescricao(descricaoBruta), [descricaoBruta]);
    // VÍDEO DO CONDOMÍNIO: `VideosEmpre` já vem na query pública do site. Só
    // renderiza quando dá pra extrair um id de YouTube válido (multi-tenant,
    // sem flag: quem cadastrou o vídeo no CRM passa a vê-lo aqui).
    const videoEmpreendimento = useMemo(
        () => parseYoutubeEmbed(empreendimentoAtual?.VideosEmpre),
        [empreendimentoAtual?.VideosEmpre]
    );
    const caracteristicas = useMemo(() => {
        const caracteristicasApi = Array.isArray(empreendimentoAtual?.caracteristicas_detalhes)
            ? (empreendimentoAtual?.caracteristicas_detalhes || [])
                .map((item) => normalizeFeatureText(String(item?.nome || '')))
                .filter(Boolean)
            : [];

        if (caracteristicasApi.length > 0) {
            return uniqueOrdered(caracteristicasApi).slice(0, 24);
        }

        return extractCaracteristicas(descricaoHtml);
    }, [descricaoHtml, empreendimentoAtual?.caracteristicas_detalhes]);

    const allDepoimentos = useMemo(
        () => (allDataHome?.depoimentos || []).filter((item: any) => item?.depoimento?.length),
        [allDataHome?.depoimentos]
    );
    const telefoneRaw = site?.Dados?.tel1_whats || site?.Dados?.cel1 || site?.Dados?.tel1 || site?.Dados?.tel2 || '';
    const telefone = normalizePhone(telefoneRaw);

    const whatsappContatoLink = telefone
        ? `https://api.whatsapp.com/send?phone=${telefone}&text=${encodeURIComponent(
              `Ola, tenho interesse no empreendimento ${empreendimentoAtual?.nome || ''}.`
          )}`
        : '';

    const whatsappVisitaLink = telefone
        ? `https://api.whatsapp.com/send?phone=${telefone}&text=${encodeURIComponent(
              `Ola, quero agendar uma visita no empreendimento ${empreendimentoAtual?.nome || ''}.`
          )}`
        : '';

    const telefoneLink = telefone ? `tel:+${telefone}` : '';
    const linkAgendamento = whatsappVisitaLink || telefoneLink;
    const agendamentoEmNovaAba = Boolean(whatsappVisitaLink);
    const leadModalMessage = empreendimentoAtual?.nome
        ? `Tenho interesse no empreendimento ${empreendimentoAtual.nome}.`
        : 'Tenho interesse neste empreendimento.';

    const mapProperties = useMemo(() => {
        if (!Array.isArray(propertiesRelatedActual)) return [];

        return propertiesRelatedActual
            .map((item) => {
                const latitude_longitude = toMapaLatLng(item?.latitude_longitude);
                if (!latitude_longitude) return null;

                return {
                    ...item,
                    id: item?.id || 0,
                    referencia: item?.referencia || item?.id || empreendimentoAtual?.id || 0,
                    latitude_longitude,
                    cidade: item?.cidade || { nome: empreendimentoAtual?.cidade || '' },
                    bairro: item?.bairro || { nome: empreendimentoAtual?.bairro || '' },
                    tipo: item?.tipo || { nome: tipoNome || 'Imovel' },
                    fotos: Array.isArray(item?.fotos) && item.fotos.length ? item.fotos : [{ url_foto: fotoDestaque?.url_foto || fallbackImage }]
                };
            })
            .filter(Boolean);
    }, [
        empreendimentoAtual?.bairro,
        empreendimentoAtual?.cidade,
        empreendimentoAtual?.id,
        fotoDestaque?.url_foto,
        propertiesRelatedActual,
        tipoNome
    ]);

    const mapPrimaryProperty = useMemo(() => {
        if (mapProperties.length > 0) return mapProperties[0];

        const latitude_longitude = toMapaLatLng(empreendimentoAtual?.latitude_longitude);
        if (!latitude_longitude) return null;

        return {
            id: empreendimentoAtual?.id || 0,
            referencia: empreendimentoAtual?.id || 0,
            latitude_longitude,
            cidade: { nome: empreendimentoAtual?.cidade || '' },
            bairro: { nome: empreendimentoAtual?.bairro || '' },
            tipo: { nome: tipoNome || 'Empreendimento' },
            fotos: [{ url_foto: fotoDestaque?.url_foto || fallbackImage }]
        };
    }, [
        empreendimentoAtual?.bairro,
        empreendimentoAtual?.cidade,
        empreendimentoAtual?.id,
        empreendimentoAtual?.latitude_longitude,
        fotoDestaque?.url_foto,
        mapProperties,
        tipoNome
    ]);

    const temMapa = Boolean(mapPrimaryProperty?.latitude_longitude);

    // CAPA: nenhum dos empreendimentos da 834 tem foto própria — o backend já
    // resolve `foto_destaque` (foto de uma UNIDADE), que é o mesmo fallback que
    // os cards de imóvel usam. Só depois disso cai na imagem genérica.
    const capaEmpreendimento =
        fotoDestaque?.url_foto || empreendimentoAtual?.foto_destaque || fallbackImage;

    const pagePath = (router.asPath || asPath || `/empreendimento/${empreendimentoAtual?.id || ''}`).split('?')[0];
    const canonicalHost = String(site?.site || prodOrigin || (typeof window !== 'undefined' ? window.location.host : '')).trim();
    const baseUrl = buildCanonical(canonicalHost, '/').replace(/\/$/, '');
    const canonicalUrl = buildCanonical(canonicalHost, pagePath);
    const withCodsite = (path: string) => appendCodsiteQuery(path, codsite, canonicalHost);
    // MULTI-FILIAIS: o link de volta pra listagem preserva a filial ativa
    // (?regiao=) quando o tenant está em modo popup; fora dele é no-op.
    const withRegiao = useRegiaoHref();
    const hrefCondominios = withRegiao(withCodsite('/condominios'));

    // O legado guarda Nome/cidade em CAIXA ALTA ("JOY XANGRI-LÁ"), o que deixava
    // title/H1 gritados. titleCasePt só age quando o texto está 100% maiúsculo —
    // cadastro já capitalizado passa intacto. Vale SÓ para os textos de SEO/
    // cabeçalho desta página; os cards seguem com o uppercase da casa.
    const nomeEmpreendimento = titleCasePt(empreendimentoAtual?.nome) || 'Empreendimento';
    const cidadeEmpreendimento = titleCasePt(empreendimentoAtual?.cidade);

    // META DESCRIPTION: a cadastrada no CRM vence; senão, a 1ª frase da descrição
    // rica (sem HTML). Teto de 155 — acima disso o Google trunca no resultado.
    const descricaoSeo = clampTexto(
        empreendimentoAtual?.meta_description ||
            primeiraFrase(descricaoBruta, 155) ||
            `Conheça o empreendimento ${nomeEmpreendimento} em ${cidadeEmpreendimento}.`,
        155
    );

    // TITLE: o cadastrado vence. Senão monta
    // "<Nome> em <Cidade> — <tipologia> à venda | <site>" e, se passar de 60,
    // corta o sufixo do site ANTES de mexer no miolo (o nome + cidade é o que
    // ranqueia). Só em último caso o clamp corta o resto.
    const nomeDoSite = String(site?.titulo || site?.Dados?.empresa || '').trim();
    const tituloSeo = useMemo(() => {
        const cadastrado = String(empreendimentoAtual?.meta_title || '').trim();
        if (cadastrado) return cadastrado;
        const tipologia = titleCasePt(tipoNome);
        const nucleo = [
            nomeEmpreendimento,
            cidadeEmpreendimento ? `em ${cidadeEmpreendimento}` : '',
        ]
            .filter(Boolean)
            .join(' ');
        const comTipologia = tipologia ? `${nucleo} — ${tipologia} à venda` : nucleo;
        const completo = nomeDoSite ? `${comTipologia} | ${nomeDoSite}` : comTipologia;
        if (completo.length <= 60) return completo;
        if (comTipologia.length <= 60) return comTipologia;
        return clampTexto(nucleo, 60);
    }, [
        cidadeEmpreendimento,
        empreendimentoAtual?.meta_title,
        nomeDoSite,
        nomeEmpreendimento,
        tipoNome,
    ]);
    const palavrasChaveSeo =
        empreendimentoAtual?.meta_keywords ||
        [empreendimentoAtual?.nome, empreendimentoAtual?.cidade, empreendimentoAtual?.bairro, 'empreendimento', 'lancamento imobiliario']
            .filter(Boolean)
            .join(', ');

    const empreendimentoSchema: Record<string, any> = {
        '@context': 'https://schema.org',
        // condomínio de casas/lotes é GatedResidenceCommunity; prédio de
        // apartamentos segue ApartmentComplex (default histórico da página).
        '@type': mapEmpreendimentoSchemaType(tipoNome, categoriaEmpreendimento),
        name: nomeEmpreendimento,
        description: descricaoSeo,
        url: canonicalUrl,
        image: fotosOrdenadas.length
            ? fotosOrdenadas.slice(0, 8).map((item) => item.url_foto)
            : [capaEmpreendimento],
        address: {
            '@type': 'PostalAddress',
            streetAddress: empreendimentoAtual?.endereco || '',
            addressLocality: empreendimentoAtual?.cidade || '',
            addressRegion: ufEmpreendimento,
            postalCode: empreendimentoAtual?.cep || '',
            addressCountry: 'BR'
        }
    };

    if (empreendimentoAtual?.preco_min || empreendimentoAtual?.preco_max) {
        empreendimentoSchema.offers = {
            '@type': 'AggregateOffer',
            priceCurrency: 'BRL',
            lowPrice: Number(empreendimentoAtual?.preco_min || 0),
            highPrice: Number(empreendimentoAtual?.preco_max || empreendimentoAtual?.preco_min || 0)
        };
    }

    // `unidades` é o TOTAL cadastrado do empreendimento (a query pública não tem
    // contagem de unidades DISPONÍVEIS), então vai como numberOfAccommodationUnits.
    const totalUnidades = Number(empreendimentoAtual?.unidades || 0);
    if (Number.isFinite(totalUnidades) && totalUnidades > 0) {
        empreendimentoSchema.numberOfAccommodationUnits = totalUnidades;
    }
    if (empreendimentoAtual?.endereco || empreendimentoAtual?.cidade) {
        empreendimentoSchema.containedInPlace = {
            '@type': 'City',
            name: empreendimentoAtual?.cidade || '',
            address: {
                '@type': 'PostalAddress',
                addressLocality: empreendimentoAtual?.cidade || '',
                addressRegion: ufEmpreendimento,
                addressCountry: 'BR',
            },
        };
    }

    // Breadcrumb pelo helper compartilhado do site (utils/schema.ts) — antes era
    // montado à mão aqui, com o mesmo formato.
    const breadcrumbSchema = buildBreadcrumbSchema([
        { name: 'Início', item: baseUrl ? `${baseUrl}/` : '/' },
        { name: 'Condomínios', item: baseUrl ? `${baseUrl}/condominios` : '/condominios' },
        { name: nomeEmpreendimento, item: canonicalUrl },
    ]);

    // FAQPage só existe quando a descrição traz a seção de perguntas.
    const faqSchema = useMemo(() => buildFaqPageSchema(faqPares), [faqPares]);

    // TOPO no formato da página do imóvel do site (Daniel 16/09): 3 e 4 = painel (gallery3; o 4 entra pelo
    // components/registry/imovel.ts), 1 e padrão = faixa com título + grade de fotos (welcome/gallery).
    // Espelha o renderGallery de pages/imoveis/[model]/.../imovel/[id].tsx — modelo novo lá precisa entrar aqui.
    const modeloTopo = [3, 4].includes(Number(site?.modelo_pagina_imovel)) ? 'painel' : 'faixa';
    // Banner "pagina_imovel" do site: o mesmo do topo do imóvel modelo 1 (imovel/[id].tsx:251-252).
    const bannerPaginaImovel = (allDataHome?.allBanners || []).find((item: any) => item?.titulo === 'pagina_imovel')?.url;
    const situacaoEmpreendimento = formatSituacao(empreendimentoAtual?.situacao);
    // H1 ÚNICO da página: nome + cidade/UF é a consulta que se quer ranquear ("<Nome> <cidade>").
    const tituloH1 = `${nomeEmpreendimento}${
        cidadeEmpreendimento ? ` — ${cidadeEmpreendimento}${ufEmpreendimento ? `/${ufEmpreendimento}` : ''}` : ''
    }`;
    const abrirFoto = (index: number) => {
        setModalInitialIndex(index);
        setModalAberto(true);
    };
    const abrirLead = () => setLeadModalOpen(true);

    const renderRodape = () => {
        // REGISTRY: variante nova de rodapé ANTES do switch legado — o lookup devolve
        // undefined para os modelos 1-5, então o diff nos ~300 sites é nulo.
        const RodapeNovo = NEW_RODAPE[Number(site?.rodape)];
        if (RodapeNovo) {
            return (
                <RodapeNovo
                    site={site}
                    allCities={allCities}
                    allCitiesTipes={allDataHome?.tipos}
                    codsite={codsite}
                    origin={prodOrigin}
                    isMobile={isMobile1}
                    allRodape={allDataHome?.rodape}
                    favorito={favoritoList}
                />
            );
        }

        switch (site?.rodape) {
            case 1:
                return (
                    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true, amount: 0.22 }}>
                        <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite} origin={prodOrigin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favoritoList} />
                    </motion.div>
                );
            case 2:
                return <AboutPieces allRodape={allDataHome?.rodape} site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite} origin={prodOrigin} isMobile={isMobile1} />;
            case 3:
                return <AboutPieces2 site={site} allRodape={allDataHome?.rodape} />;
            case 4:
                return (
                    <MegaFooter
                        site={site}
                        allCities={allCities}
                        codsite={codsite}
                        origin={prodOrigin}
                        isMobile={isMobile1}
                        favorito={favoritoList}
                        allRodape={allDataHome?.rodape}
                        fallback={<AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite} origin={prodOrigin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favoritoList} />}
                    />
                );
            case 5:
                return (
                    <EditorialFooter
                        site={site}
                        allCities={allCities}
                        codsite={codsite}
                        origin={prodOrigin}
                        isMobile={isMobile1}
                        favorito={favoritoList}
                        allRodape={allDataHome?.rodape}
                        fallback={<AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite} origin={prodOrigin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favoritoList} />}
                    />
                );
            default:
                return (
                    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true, amount: 0.22 }}>
                        <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite} origin={prodOrigin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favoritoList} />
                    </motion.div>
                );
        }
    };

    // Modelos prontos #7 Aurora (topo 10) · #8 Horizonte (11) · #9 Atelier (12) · #10 Panorama (13): a página no visual
    // da home do modelo, como /imoveis e a ficha. Os 4: atributo data-modelo + CSS vars na raiz e regras
    // `[data-modelo='x'] .classe` nos módulos CSS da página; o Atelier soma a paleta (siteComPaletaAtelier) e a classe .interna do
    // AtelierInterna.module.css. Fora dos topos 10–13: modeloInterna/varsModelo vazios ⇒ DOM e estilo de sempre (zero diff).
    const modeloInterna = ehModeloHorizonte(site) ? 'horizonte' : ehModeloAurora(site) ? 'aurora' : ehModeloPanorama(site) ? 'panorama' : ehModeloLarissa(site) ? 'larissa' : ehModeloMetropole(site) ? 'metropole' : ehModeloInternacional(site) ? 'internacional' : ehModeloNorte(site) ? 'norte' : ehModeloCampo(site) ? 'campo' : paginaInternaAtelier(site) ? 'atelier' : undefined;
    const atelierInterna = paginaInternaAtelier(site);
    const varsModelo = (modeloInterna === 'horizonte' ? varsHorizonteInterna(site) : modeloInterna === 'aurora' ? varsAuroraEmpreendimento(site) : modeloInterna === 'panorama' ? varsPanoramaEmpreendimento(site) : modeloInterna === 'larissa' ? varsLarissaInterna(site) : modeloInterna === 'metropole' ? varsMetropoleInterna(site) : modeloInterna === 'internacional' ? varsInternacionalPagina(site) : modeloInterna === 'norte' ? varsNortePagina(site) : modeloInterna === 'campo' ? varsCampoPagina(site) : atelierInterna ? varsAtelier(site) : null) as CSSProperties | null;
    // Larissa (topo 14): títulos de seção em dois tons (TituloLr) e unidades na grade do LarissaCard (UnidadesLarissa)
    const larissa = modeloInterna === 'larissa';
    // Metrópole (topo 15): mesma pele em raiz (data-modelo + vars --mt-*) e as unidades na grade do MetropoleCard (UnidadesMetropole);
    // os títulos de seção seguem o texto de sempre (o CSS do modelo os deixa leves, de um tom só)
    const metropole = modeloInterna === 'metropole';
    const internacional = modeloInterna === 'internacional';
    // Norte (topo 17): skin em [data-modelo='norte'] (empreendimento.module.css) + unidades na grade do NorteCard.
    const norte = modeloInterna === 'norte';
    // Campo (topo 18): skin em [data-modelo='campo'] (empreendimento.module.css: papel, cartões raio 20, pílulas) +
    // unidades na grade do CampoCard.
    const campo = modeloInterna === 'campo';

    if (carregando) {
        return (
            <>
                {site ? <WelcomePiecesProperties2 site={site} favorito={favoritoList} props={allDataHome} /> : null}
                <div className={styles.loadingWrap}><div className={styles.loadingBox}><div className={styles.loadingPulse} /><p>Carregando empreendimento...</p></div></div>
                {site ? renderRodape() : null}
            </>
        );
    }

    if (!empreendimentoAtual?.id) {
        return (
            <>
                <Head><title>Empreendimento nao encontrado</title><meta name="robots" content="noindex, nofollow" /></Head>
                {site ? <WelcomePiecesProperties2 site={site} favorito={favoritoList} props={allDataHome} /> : null}
                <section className={styles.emptyWrap}><h1>Empreendimento nao encontrado</h1><p>Nao foi possivel carregar os dados deste empreendimento.</p><Link href={withCodsite('/condominios')} className={styles.emptyAction}>Voltar para empreendimentos</Link></section>
                {site ? renderRodape() : null}
            </>
        );
    }
    return (
        <>
            <Head>
                <title>{tituloSeo}</title>
                <meta name="description" content={descricaoSeo} />
                <meta name="keywords" content={palavrasChaveSeo} />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content={tituloSeo} />
                <meta property="og:description" content={descricaoSeo} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={capaEmpreendimento} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={tituloSeo} />
                <meta name="twitter:description" content={descricaoSeo} />
                <meta name="twitter:image" content={capaEmpreendimento} />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(empreendimentoSchema) }} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
                {faqSchema ? (
                    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
                ) : null}
            </Head>

            {site ? <WelcomePiecesProperties2 site={site} favorito={favoritoList} props={allDataHome} /> : null}

            <div className={`${styles.page}${atelierInterna ? ` ${atInterna.interna}` : ''}${internacional ? ` ${inInterna.interna}` : ''}${norte ? ` ${ntInterna.interna}` : ''}${campo ? ` ${cpInterna.interna}` : ''}`} style={varsModelo ? ({ ...pageThemeVars, ...varsModelo } as CSSProperties) : pageThemeVars} data-modelo={modeloInterna} data-fundo={larissa || metropole ? (isColorLight(site?.cor5 || '#ffffff') ? 'claro' : 'escuro') : internacional ? 'claro' : undefined}>
                {modeloTopo === 'painel' ? (
                    <>
                        <TopoPainelEmpreendimento
                            titulo={tituloH1}
                            nome={nomeEmpreendimento}
                            situacao={situacaoEmpreendimento}
                            localizacao={localizacaoCompleta}
                            precoInicial={precoInicial}
                            faixaPreco={faixaPreco}
                            precosConversor={temConversorMoeda(site) ? { min: Number(empreendimentoAtual?.preco_min) || 0, max: Number(empreendimentoAtual?.preco_max) || 0 } : undefined}
                            faixaArea={faixaArea}
                            logo={empreendimentoAtual?.logo}
                            fotos={fotosGaleria}
                            capa={capaEmpreendimento}
                            temMapa={temMapa}
                            temVideo={Boolean(videoEmpreendimento)}
                            whatsappContatoLink={whatsappContatoLink}
                            linkAgendamento={linkAgendamento}
                            agendamentoEmNovaAba={agendamentoEmNovaAba}
                            onAbrirFoto={abrirFoto}
                            onAbrirMapa={() => setOpenMapa(1)}
                            onAbrirLead={abrirLead}
                        />
                        <BreadcrumbEmpreendimento hrefInicio={withCodsite('/')} hrefCondominios={hrefCondominios} nome={nomeEmpreendimento} />
                    </>
                ) : (
                    <>
                        <FaixaTituloEmpreendimento
                            titulo={tituloH1}
                            nome={nomeEmpreendimento}
                            situacao={situacaoEmpreendimento}
                            banner={bannerPaginaImovel || capaEmpreendimento}
                        />
                        <BreadcrumbEmpreendimento hrefInicio={withCodsite('/')} hrefCondominios={hrefCondominios} nome={nomeEmpreendimento} />
                        {/* Internacional: com menos de 2 fotos a grade só REPETIA a foto do banner logo acima (cs1722) — some */}
                        {(internacional || norte || campo) && fotosGaleria.length < 2 ? null : (
                            <GradeFotosEmpreendimento
                                nome={nomeEmpreendimento}
                                fotos={fotosGaleria}
                                capa={capaEmpreendimento}
                                onAbrirFoto={abrirFoto}
                            />
                        )}
                    </>
                )}

                <div className={styles.contentShell}>
                    {/* Detalhes + coluna de contato à direita, como no imóvel. O <aside> vem DEPOIS no DOM (a
                        descrição segue sendo o 1º texto depois do h1); no celular o grid põe o contato antes. */}
                    <div className={styles.detailsGrid}>
                        <div className={styles.detailsMain}>
                            {modeloTopo === 'faixa' ? (
                                <PrecoFaixaEmpreendimento precoInicial={precoInicial} faixaPreco={faixaPreco} faixaArea={faixaArea} />
                            ) : null}

                            <nav className={styles.sectionNav}>
                                <a href="#empreendimento" className={styles.sectionLink}>O Empreendimento</a>
                                {fotosPlantas.length > 0 ? <a href="#plantas" className={styles.sectionLink}>Plantas</a> : null}
                                <a href="#sobre" className={styles.sectionLink}>Sobre</a>
                                <a href="#galeria" className={styles.sectionLink}>Galeria</a>
                                {videoEmpreendimento ? <a href="#video" className={styles.sectionLink}>Vídeo</a> : null}
                                <a href="#unidades" className={styles.sectionLink}>Unidades</a>
                                <a href="#contato" className={styles.sectionLink}>Contato</a>
                                {temMapa ? <a href="#mapa" className={styles.sectionLink}>Mapa</a> : null}
                            </nav>

                            {resumoTags.length > 0 ? (
                                <div className={styles.tagsRow}>
                                    {resumoTags.map((tag) => (
                                        <span key={tag.key} className={styles.infoTag}>
                                            <ResumoTagIcon type={tag.icon} />
                                            <span>{tag.label}</span>
                                        </span>
                                    ))}
                                </div>
                            ) : null}

                            {categoriaEmpreendimento || tiposEmpreendimento.length > 0 ? (
                                <div className={styles.tagsRow}>
                                    {categoriaEmpreendimento ? (
                                        <span className={styles.infoTag}>
                                            <span><strong>Categoria:</strong> {categoriaEmpreendimento}</span>
                                        </span>
                                    ) : null}
                                    {tiposEmpreendimento.length > 0 ? (
                                        <span className={styles.infoTag}>
                                            <span><strong>Tipos:</strong> {tiposEmpreendimento.join(', ')}</span>
                                        </span>
                                    ) : null}
                                </div>
                            ) : null}

                            {/* SEO/GEO: a DESCRIÇÃO é o 1º bloco depois do H1 — é o texto que
                                o Google indexa e que a IA lê para citar a página. Vem
                                sanitizado (allowlist de tags) de utils/descricaoRica. */}
                            <section id="sobre" className={styles.sectionBlock}>
                                <header className={styles.sectionHeader}><h2>{larissa ? <TituloLr forte="Sobre o" suave={nomeEmpreendimento} /> : <>Sobre o {nomeEmpreendimento}</>}</h2></header>

                                <div className={styles.descriptionLayout}>
                                    <div className={styles.descriptionMain}>
                                        <div className={styles.descriptionHtml} dangerouslySetInnerHTML={{ __html: descricaoHtml }} />

                                        <div className={styles.featuresBlock}>
                                            <h3>Características</h3>
                                            {caracteristicas.length > 0 ? (
                                                <ul className={styles.featuresGrid}>{caracteristicas.map((item) => <li key={item}>{item}</li>)}</ul>
                                            ) : <p className={styles.emptyMessage}>Entre em contato para receber a lista completa de características.</p>}
                                        </div>

                                        {/* link interno SSR: leva o crawler (e o visitante) de volta
                                            à listagem de condomínios, com a filial preservada. */}
                                        <p className={styles.descriptionCta}>
                                            <Link prefetch={false} href={hrefCondominios} className={styles.descriptionCtaLink}>
                                                {cidadeEmpreendimento
                                                    ? `Ver todos os condomínios de ${cidadeEmpreendimento}`
                                                    : 'Ver todos os condomínios'}
                                            </Link>
                                        </p>
                                    </div>
                                </div>
                            </section>

                            {fotosPlantas.length > 0 ? (
                                <section id="plantas" className={styles.sectionBlock}>
                                    <header className={styles.sectionHeader}>
                                        <h2>{larissa ? <TituloLr forte="Plantas" suave="do empreendimento" /> : 'Plantas do empreendimento'}</h2>
                                        <p>Selecione uma planta para visualizar.</p>
                                    </header>

                                    <div className={styles.plantaTabs}>
                                        {fotosPlantas.map((foto, index) => (
                                            <button key={`${foto.id}-${index}`} type="button" className={`${styles.plantaTab} ${index === plantaAtivaIndex ? styles.plantaTabActive : ''}`} onClick={() => setPlantaAtivaIndex(index)}>
                                                {foto?.descricao?.trim() || `Planta ${index + 1}`}
                                            </button>
                                        ))}
                                    </div>

                                    <div className={styles.plantaViewer}>
                                        <Image src={fotosPlantas[plantaAtivaIndex]?.url_foto || fallbackImage} alt={fotosPlantas[plantaAtivaIndex]?.descricao || `Planta ${plantaAtivaIndex + 1}`} fill sizes="(max-width: 1200px) 100vw, 1080px" className={styles.plantaImage} />
                                    </div>
                                </section>
                            ) : null}

                            <section id="galeria" className={styles.sectionBlock}>
                                <header className={styles.sectionHeader}>
                                    <h2>{larissa ? <TituloLr forte="Galeria" suave="de fotos" /> : 'Galeria de fotos'}</h2>
                                    <p>Clique em uma foto para abrir o slider em fullscreen.</p>
                                </header>

                                {fotosGaleria.length > 0 ? (
                                    <div className={styles.galleryGrid}>
                                        {fotosGaleria.slice(0, 5).map((foto, index) => (
                                            <button key={`${foto.id}-${index}`} type="button" className={`${styles.galleryItem} ${index === 0 ? styles.galleryMain : ''}`} onClick={() => abrirFoto(index)}>
                                                <Image src={foto.url_foto} alt={foto.descricao || `Foto ${index + 1}`} fill sizes={index === 0 ? '(max-width: 1024px) 100vw, 62vw' : '(max-width: 1024px) 50vw, 26vw'} className={styles.galleryImage} />
                                                {index === 4 && fotosGaleria.length > 5 ? <span className={styles.galleryMore}>+{fotosGaleria.length - 5}</span> : null}
                                            </button>
                                        ))}
                                    </div>
                                ) : <p className={styles.emptyMessage}>Nenhuma foto encontrada para este empreendimento.</p>}
                            </section>

                            {videoEmpreendimento ? (
                                <section id="video" className={styles.sectionBlock}>
                                    <header className={styles.sectionHeader}><h2>{larissa ? <TituloLr forte="Vídeo" suave="do condomínio" /> : 'Vídeo do condomínio'}</h2></header>
                                    <div
                                        className={`${styles.videoFrame} ${videoEmpreendimento.vertical ? styles.videoVertical : styles.videoHorizontal}`}
                                    >
                                        <iframe
                                            src={videoEmpreendimento.src}
                                            title={`Vídeo do empreendimento ${empreendimentoAtual?.nome || ''}`}
                                            loading="lazy"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            referrerPolicy="strict-origin-when-cross-origin"
                                            allowFullScreen
                                        />
                                    </div>
                                </section>
                            ) : null}
                        </div>

                        <aside id="contato" className={styles.detailsAside}>
                            <div className={styles.asideBox}>
                                <ColunaContatoEmpreendimento
                                    whatsappContatoLink={whatsappContatoLink}
                                    linkAgendamento={linkAgendamento}
                                    agendamentoEmNovaAba={agendamentoEmNovaAba}
                                    onAbrirLead={abrirLead}
                                />
                            </div>
                        </aside>
                    </div>

                    {propertiesRelatedActual?.length > 0 ? (
                        <section id="unidades" className={`${styles.sectionBlock} ${styles.unitsSection}`}>
                            {larissa ? (
                                <UnidadesLarissa imoveis={propertiesRelatedActual} site={site} codsite={codsite} favorito={favoritoList} nome={nomeEmpreendimento} />
                            ) : metropole ? (
                                <UnidadesMetropole imoveis={propertiesRelatedActual} site={site} codsite={codsite} favorito={favoritoList} nome={nomeEmpreendimento} />
                            ) : internacional ? (
                                <UnidadesInternacional imoveis={propertiesRelatedActual} site={site} codsite={codsite} favorito={favoritoList} nome={nomeEmpreendimento} />
                            ) : norte ? (
                                <UnidadesNorte imoveis={propertiesRelatedActual} site={site} codsite={codsite} favorito={favoritoList} nome={nomeEmpreendimento} />
                            ) : campo ? (
                                <UnidadesCampo imoveis={propertiesRelatedActual} site={site} codsite={codsite} favorito={favoritoList} nome={nomeEmpreendimento} />
                            ) : (
                            <div className={styles.unitsCarousel}>
                                <RecommendationPiecesProperties propertiesRelatedActual={propertiesRelatedActual} site={site} favorito={favoritoList} setFavorito={setFavorito} imoveisEmpreendimento={true} allViewProperties={null} />
                            </div>
                            )}
                        </section>
                    ) : null}
                    
                    {temMapa ? (
                        <section id="mapa" className={styles.mapSection}>
                            <div className={styles.mapBand}>
                                <div className={styles.mapAddressBlock}>
                                    <p className={styles.mapLabel}>Localização:</p>
                                    <strong>{localizacaoCompleta || `${empreendimentoAtual?.cidade || ''}`}</strong>
                                </div>

                                <div className={styles.mapActions}><button type="button" className={styles.mapButton} onClick={() => setOpenMapa(1)}>Mapa</button></div>
                            </div>
                        </section>
                    ) : null}

                    {allDepoimentos.length > 0 && site?.modelo_depoimento > 0 ? (
                        <section className={styles.depoimentosWrap}>
                            {internacional ? (
                                <DepoimentosInternacional slides={allDepoimentos} title="DEPOIMENTOS" site={site}/>
                            ) : norte ? (
                                <DepoimentosNorte slides={allDepoimentos} title="DEPOIMENTOS" site={site}/>
                            ) : campo ? (
                                <DepoimentosCampo slides={allDepoimentos} title="Depoimentos" site={site}/>
                            ) : site?.modelo_depoimento == 4 ? (
                                <DepoimentosGrid slides={allDepoimentos} title="DEPOIMENTOS" site={site}/>
                            ) : site?.modelo_depoimento === 2 ? (
                                <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true, amount: 0.22 }}>
                                    <DepoimentoCard2 slides={allDepoimentos} title="DEPOIMENTOS" site={site} />
                                </motion.div>
                            ) : (
                                <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true, amount: 0.22 }}>
                                    <DepoimentoCard slides={allDepoimentos} title="DEPOIMENTOS" site={site} />
                                </motion.div>
                            )}
                        </section>
                    ) : null}
                </div>
                {modalAberto && fotosGaleria.length > 0 ? (
                    <div className={styles.modalBackdrop} onClick={() => setModalAberto(false)}>
                        <button type="button" className={styles.modalClose} onClick={() => setModalAberto(false)} aria-label="Fechar galeria">
                            <IoCloseSharp size={28} />
                        </button>

                        <div className={styles.modalFrame} onClick={(event) => event.stopPropagation()}>
                            <Swiper key={`${modalInitialIndex}-${fotosGaleria.length}`} modules={[Navigation, Keyboard]} navigation keyboard={{ enabled: true }} initialSlide={modalInitialIndex} className={styles.modalSwiper}>
                                {fotosGaleria.map((foto, index) => (
                                    <SwiperSlide key={`${foto.id}-${index}`} className={styles.modalSlide}>
                                        <div className={styles.modalImageWrap}>
                                            <Image src={foto.url_foto} alt={foto?.descricao || `Foto ${index + 1}`} fill sizes="100vw" className={styles.modalImage} />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </div>
                ) : null} 

                {temMapa && openMapa && mapPrimaryProperty && openMapa ? (
                    <Mapa
                        prop={mapProperties[0] || mapPrimaryProperty}
                        properties={mapProperties.length > 0 ? mapProperties : [mapPrimaryProperty]}
                        tipo={0}
                        popup={0}
                        openMapa={1}
                        setOpenMapa={setOpenMapa}
                    />
                ) : null}

                {site ? (
                    <LeadContactModal
                        open={leadModalOpen}
                        onClose={() => setLeadModalOpen(false)}
                        site={site}
                        origin={prodOrigin}
                        codsite={codsite}
                        title="Receba contato em 3 minutos"
                        message={leadModalMessage}
                        source="empreendimento-receba-3min"
                        entityId={0}
                    />
                ) : null}
            </div>

            {site ? (campo ? <div className={cpInterna.rodape} style={varsCampoRodape(site) || undefined}>{renderRodape()}</div> : renderRodape()) : null}
            {/* SD-0017: WhatsApp flutuante na página do empreendimento para todos os sites (antes só cs2639) */}
            {site ? <WhatsAppComponent site={site} codsite={codsite} /> : null}
        </>
    );
};

export default EmpreendimentoPage;
